import { faker } from '@faker-js/faker'

import {
  Ad,
  AdExposureAvailability,
  AdGroup,
  AdGroupSummary,
  AdSummary,
  AgeRange,
  AgeTargeting,
  Bid,
  Campaign,
  CampaignStatus,
  CampaignSummary,
  CampaignType,
  Conversion,
  CreateAdGroupRequestBody,
  CreateAdRequestBody,
  CreateCampaignRequestBody,
  DailyAdPerformance,
  DailyTargetingPerformance,
  DeleteCampaignRequestBody,
  EditAdGroupRequestBody,
  EditCampaignRequestBody,
  Gender,
  GenderTargeting,
  GetCampaignPerformanceSummaryRequestBody,
  GetCampaignPerformanceSummaryResponse,
  GetCampaignRequestBody,
  GetCampaignSummariesRequestBody,
  GetCampaignSummariesResponse,
  GetDailyAdPerformancesRequestBody,
  GetDailyAdPerformancesResponse,
  GetDailyTargetingPerformancesRequestBody,
  GetDailyTargetingPerformancesResponse,
  Language,
  LanguageTargeting,
  ServiceOffer,
  SwitchOffCampaignRequestBody,
  SwitchOnCampaignRequestBody,
  TargetingType,
  TreatmentTargeting,
} from '../../contracts/performance-ad'
import { asBuilder } from '../asBuilder'
import { getSupernovaPagedBody } from '../foundation/pagination'

export * from './PartnerServiceOfferList'
export * from './PartnerServiceOfferListCriteria'

const randomEnum = <T extends { [index: string]: string | number }>(anEnum: T): T[keyof T] => {
  const enumValues = Object.values(anEnum) as Array<T[keyof T]>
  const randomIndex = Math.floor(Math.random() * enumValues.length)
  return enumValues[randomIndex]
}

export const getRandomAd: () => Ad = () => ({
  id: faker.string.uuid(),
  adGroupId: faker.string.uuid(),
  status: randomEnum(AdExposureAvailability),
  serviceOfferId: faker.number.int(),
})

const getRandomBid = (): Bid => {
  return {
    pricingModel: 'CPC',
    amount: faker.number.int({ min: 100, max: 10000 }),
    currency: 'KRW',
  }
}

const getRandomTreatmentTargeting = (): TreatmentTargeting => {
  return {
    id: faker.string.uuid(),
    type: TargetingType.TREATMENT,
    value: faker.number.int(),
  }
}

const getRandomLanguageTargeting = (): LanguageTargeting => {
  return {
    id: faker.string.uuid(),
    type: TargetingType.LANGUAGE,
    value: randomEnum(Language),
  }
}

export const getRandomGenderTargeting = asBuilder((): GenderTargeting => {
  return {
    id: faker.string.uuid(),
    type: TargetingType.GENDER,
    value: randomEnum(Gender),
  }
})

export const getRandomAgeTargeting = asBuilder((): AgeTargeting => {
  return {
    id: faker.string.uuid(),
    type: TargetingType.AGE_RANGE,
    value: randomEnum(AgeRange),
  }
})

export const getRandomAdGroup = asBuilder((): AdGroup => {
  return {
    id: faker.string.uuid(),
    campaignId: faker.string.uuid(),
    targetings: [
      ...Array.from({ length: faker.number.int({ min: 1, max: 5 }) }, getRandomTreatmentTargeting),
      getRandomLanguageTargeting(),
    ],
    bid: getRandomBid(),
  }
})

export const getRandomCampaign = asBuilder(
  (): Campaign => ({
    id: faker.string.uuid(),
    type: randomEnum(CampaignType),
    hospitalId: faker.number.int(),
    name: faker.company.name(),
    dailyBudget: {
      date: faker.date.recent().toISOString(),
      amount: faker.number.int(),
      spendingAmount: faker.number.int(),
      currency: 'KRW',
    },
    status: randomEnum(CampaignStatus),
    adExposureAvailability: randomEnum(AdExposureAvailability),
  }),
)

export const getRandomCreateCampaignRequestBody: () => CreateCampaignRequestBody = () => ({
  id: faker.string.uuid(),
  type: randomEnum(CampaignType),
  name: faker.company.name(),
  dailyBudgetAmount: faker.number.int({ min: 0, max: 10000 }),
  dailyBudgetCurrency: 'KRW',
})

export const getRandomEditCampaignRequestBody: () => EditCampaignRequestBody = () => ({
  id: faker.string.uuid(),
  name: faker.company.name(),
  dailyBudgetAmount: faker.number.int({ min: 0, max: 10000 }),
  dailyBudgetCurrency: 'KRW',
})

export const getRadomDeleteCampaignRequestBody: () => DeleteCampaignRequestBody = () => ({
  id: faker.string.uuid(),
})

export const getRandomSwitchOnCampaignRequestBody: () => SwitchOnCampaignRequestBody = () => ({
  id: faker.string.uuid(),
})

export const getRandomSwitchOffCampaignRequestBody: () => SwitchOffCampaignRequestBody = () => ({
  id: faker.string.uuid(),
})

export const getRandomCreateAdGroupRequestBody: () => CreateAdGroupRequestBody = () => ({
  ...getRandomAdGroup(),
})

export const getRandomEditAdGroupRequestBody: () => EditAdGroupRequestBody = () => {
  const { campaignId, ...rest } = getRandomAdGroup()
  return rest
}

export const getRandomCreateAdRequestBody: () => CreateAdRequestBody = () => ({
  id: faker.string.uuid(),
  adGroupId: faker.string.uuid(),
  serviceOfferId: faker.number.int(),
})

const getRandomAdGroupSummary = (): AdGroupSummary => {
  return {
    id: faker.string.uuid(),
    sequentialId: faker.number.int(),
    bid: getRandomBid(),
    targetings: [
      ...Array.from({ length: faker.number.int({ min: 1, max: 5 }) }, getRandomTreatmentTargeting),
      getRandomLanguageTargeting(),
    ],
  }
}

const getRandomAdSummary = (): AdSummary => {
  return {
    id: faker.string.uuid(),
    sequentialId: faker.number.int(),
    serviceOfferId: faker.number.int(),
    serviceOfferName: faker.commerce.productName(),
  }
}

export const getRandomCampaignSummary = asBuilder((): CampaignSummary => {
  return {
    id: faker.string.uuid(),
    sequentialId: faker.number.int(),
    name: faker.string.uuid().slice(0, 5),
    status: randomEnum(CampaignStatus),
    adExposureAvailability: randomEnum(AdExposureAvailability),
    dailyBudget: {
      date: faker.date.recent().toISOString(),
      amount: faker.number.int(),
      spendingAmount: faker.number.int(),
      currency: 'KRW',
    },
    adGroup: getRandomAdGroupSummary(),
    ad: getRandomAdSummary(),
  }
})

export const getRandomGetCampaignRequestBody = (): GetCampaignRequestBody => {
  return {
    id: faker.string.uuid(),
  }
}
export const getRandomCampaignSummaries = (): CampaignSummary[] => {
  return Array.from({ length: faker.number.int({ min: 1, max: 100 }) }, getRandomCampaignSummary)
}

export const getRandomCampaignSummariesResponse = asBuilder((): GetCampaignSummariesResponse => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return getSupernovaPagedBody((base) => {
    base.contents = getRandomCampaignSummaries()
  })
})

export const getRandomGetCampaignSummariesRequestBody = (): GetCampaignSummariesRequestBody => {
  return {
    filter: {
      type: randomEnum(CampaignType),
      statuses: [randomEnum(CampaignStatus)],
      adExposureAvailability: randomEnum(AdExposureAvailability),
    },
    pagination: {
      pageIndex: faker.number.int({ min: 1, max: 100 }),
      pageSize: faker.number.int({ min: 1, max: 100 }),
    },
  }
}

const getRandomPagination = () => {
  return {
    pageSize: faker.number.int({ min: 1, max: 100 }),
    pageIndex: faker.number.int({ min: 0, max: 10 }),
  }
}

export const getRandomGetDailyAdPerformancesRequestBody = asBuilder((): GetDailyAdPerformancesRequestBody => {
  return {
    filter: {
      campaignIds: [faker.string.uuid()],
      period: {
        fromDate: faker.date.past().toISOString().split('T')[0],
        toDate: faker.date.recent().toISOString().split('T')[0],
      },
    },
    pagination: getRandomPagination(),
  }
})

export const getRandomGetCampaignPerformanceSummaryRequestBody = asBuilder(
  (): GetCampaignPerformanceSummaryRequestBody => {
    return {
      filter: {
        campaignId: faker.string.uuid(),
        period: {
          fromDate: faker.date.past().toISOString().split('T')[0],
          toDate: faker.date.recent().toISOString().split('T')[0],
        },
      },
    }
  },
)

export const getRandomGetDailyTargetingPerformancesRequestBody = asBuilder(
  (): GetDailyTargetingPerformancesRequestBody => {
    return {
      filter: {
        targetingType: faker.string.uuid(),
        campaignId: faker.string.uuid(),
        period: {
          fromDate: faker.date.past().toISOString().split('T')[0],
          toDate: faker.date.recent().toISOString().split('T')[0],
        },
      },
      pagination: getRandomPagination(),
    }
  },
)

export const getRandomDailyTargetingPerformance = asBuilder((): DailyTargetingPerformance => {
  return {
    statisticsDate: faker.date.recent().toISOString().split('T')[0],
    targeting: {
      id: faker.string.uuid(),
      type: 'TREATMENT',
      value: faker.number.int(),
      name: faker.string.uuid(),
    },
    currency: 'KRW',
    adId: faker.string.uuid(),
    adGroupId: faker.string.uuid(),
    campaignId: faker.string.uuid(),
    hospitalId: faker.number.int(),
    viewCount: faker.number.int(),
    clickCount: faker.number.int(),
    clickThroughRate: parseFloat(faker.finance.amount(0, 1, 4)),
    totalCost: faker.number.int(),
    costPerClick: parseFloat(faker.finance.amount(0, 1000, 6)),
  }
})

export const getRandomGetDailyTargetingPerformancesResponse = asBuilder((): GetDailyTargetingPerformancesResponse => {
  const contents = Array.from({ length: faker.number.int({ min: 101, max: 201 }) }, getRandomDailyTargetingPerformance)

  return {
    totalPageNumber: faker.number.int({ min: 1, max: 10 }),
    currentPageIndex: faker.number.int({ min: 1, max: contents.length }),
    pageSize: contents.length,
    isLastPage: faker.datatype.boolean(),
    totalElementNumber: contents.length,
    contents,
  }
})

const getRandomConversion = asBuilder((): Conversion => {
  return {
    firstClickBasedPerformance: {
      count: faker.number.int(),
      value: {
        amount: faker.number.int(),
        currency: 'KRW',
      },
    },
    lastClickBasedPerformance: {
      count: faker.number.int(),
      value: {
        amount: faker.number.int(),
        currency: 'KRW',
      },
    },
    assistedClickCount: faker.number.int(),
  }
})

export const getRandomCampaignPerformanceSummaryResponse = asBuilder((): GetCampaignPerformanceSummaryResponse => {
  return {
    hospitalId: faker.number.int(),
    campaignId: faker.string.uuid(),
    viewCount: faker.number.int(),
    clickCount: faker.number.int(),
    clickThroughRate: parseFloat(faker.finance.amount(0, 1, 4)),
    costPerClick: parseFloat(faker.finance.amount(0, 1000, 6)),
    totalCost: faker.number.int(),
    conversion: getRandomConversion(),
    currency: 'KRW',
  }
})

const getRandomServiceOffer = asBuilder((): ServiceOffer => {
  return {
    id: faker.number.int(),
    name: faker.commerce.product(),
  }
})

export const getRandomDailyAdPerformance = asBuilder((): DailyAdPerformance => {
  return {
    statisticsDate: faker.date.recent().toISOString().split('T')[0],
    adId: faker.string.uuid(),
    adGroupId: faker.string.uuid(),
    campaignId: faker.string.uuid(),
    currency: 'KRW',
    serviceOffer: getRandomServiceOffer(),
    hospitalId: faker.number.int(),
    viewCount: faker.number.int(),
    clickCount: faker.number.int(),
    clickThroughRate: parseFloat(faker.finance.amount(0, 1, 4)),
    totalCost: faker.number.int(),
    costPerClick: parseFloat(faker.finance.amount(0, 1000, 6)),
    conversion: getRandomConversion(),
  }
})

export const getRandomDailyAdPerformancesResponse = asBuilder((): GetDailyAdPerformancesResponse => {
  const contents = Array.from({ length: faker.number.int({ min: 101, max: 201 }) }, getRandomDailyAdPerformance)

  return {
    totalPageNumber: faker.number.int({ min: 1, max: 10 }),
    currentPageIndex: faker.number.int({ min: 1, max: contents.length }),
    pageSize: contents.length,
    isLastPage: faker.datatype.boolean(),
    totalElementNumber: contents.length,
    contents,
  }
})
